import React, { useEffect, useMemo, useState } from "react";
import { useAuthContext } from "@/lib/auth-context";

const CORE_API = import.meta.env.VITE_CORE_API_URL || "";

type AiTool = {
  id: string;
  kind: string;
  owner_role: string;
  tc_cost: number | null;
  meta: any;
};

type UserToolSetting = {
  tool_id: string;
  enabled: boolean;
  settings: any;
};

type RoleAIToolsPageProps = {
  role: "teacher" | "coach";
  title: string;
  description: string;
};

export const RoleAIToolsPage: React.FC<RoleAIToolsPageProps> = ({
  role,
  title,
  description,
}) => {
  const { session } = useAuthContext();
  const accessToken = session?.access_token;
  const [tools, setTools] = useState<AiTool[]>([]);
  const [userTools, setUserTools] = useState<UserToolSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingToolId, setSavingToolId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const headers: Record<string, string> = {};
        if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

        const [toolsRes, meRes] = await Promise.all([
          fetch(`${CORE_API}/api/v1/marketplace/ai-tools?role=${role}`, { headers }),
          fetch(`${CORE_API}/api/v1/marketplace/me-tools?role=${role}`, { headers }),
        ]);

        const toolsJson = await toolsRes.json();
        const meJson = await meRes.json();

        if (!toolsRes.ok) {
          throw new Error(toolsJson?.error || "Failed to load AI tools catalog");
        }

        if (!meRes.ok) {
          throw new Error(meJson?.error || "Failed to load your AI tools");
        }

        if (!cancelled) {
          setTools(toolsJson.tools || []);
          setUserTools(meJson.tools || []);
        }
      } catch (err: any) {
        console.error("Failed to load AI tools", err);
        if (!cancelled) setError(err?.message || "Could not load AI tools.");
      } finally {
        if (!cancelled) setLoading(false);
      }
      }

    load();
    return () => {
      cancelled = true;
    };
  }, [role, accessToken]);

  const activeMap = useMemo(() => {
    return new Set(
      (userTools || []).filter((t) => t.enabled).map((t) => t.tool_id)
    );
  }, [userTools]);

  const toggleTool = async (toolId: string, enabled: boolean) => {
    try {
      setSavingToolId(toolId);
      setError(null);
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

      const res = await fetch(`${CORE_API}/api/v1/marketplace/ai-tools/toggle`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          toolId,
          enabled,
          role,
          surface: role === "teacher" ? "teacher-portal" : "coach-portal",
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error || "Failed to toggle tool");
      }

      const meRes = await fetch(`${CORE_API}/api/v1/marketplace/me-tools?role=${role}`, { headers });
      const meJson = await meRes.json();

      if (!meRes.ok) {
        throw new Error(meJson?.error || "Failed to refresh your AI tools");
      }

      setUserTools(meJson.tools || []);
    } catch (err: any) {
      console.error("toggle tool failed", err);
      setError(err?.message || "Could not update tool selection.");
    } finally {
      setSavingToolId(null);
    }
  };

  if (loading) return <div>Loading AI tools…</div>;
  if (error)
    return <div className="text-sm text-red-600">{error || "Error loading tools."}</div>;

  if (!tools.length) {
    return (
      <div className="space-y-2">
        <h1 className="text-xl font-semibold">{title}</h1>
        <p className="text-sm text-slate-600">{description}</p>
        <div className="text-sm text-slate-500">
          No AI tools are available right now.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">{title}</h1>
        <p className="text-sm text-slate-600">{description}</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {tools.map((tool) => {
          const active = activeMap.has(tool.id);
          const saving = savingToolId === tool.id;
          const label = tool.meta?.label || tool.meta?.summary || tool.id;
          const descriptionText =
            tool.meta?.description || tool.meta?.summary || "";

          return (
            <div
              key={tool.id}
              className="flex flex-col justify-between rounded-md border bg-white p-3"
            >
              <div>
                <div className="text-sm font-medium text-slate-900">{label}</div>
                {descriptionText && (
                  <div className="mt-1 text-xs text-slate-600 line-clamp-3">
                    {descriptionText}
                  </div>
                )}
                {tool.tc_cost != null && tool.tc_cost > 0 && (
                  <div className="mt-2 text-xs text-slate-700">
                    Cost per use: {tool.tc_cost} TC
                  </div>
                )}
              </div>

              <div className="mt-3 flex items-center justify-between">
                <span
                  className={`text-xs ${
                    active ? "text-emerald-600" : "text-slate-500"
                  }`}
                >
                  {active ? "Active" : "Inactive"}
                </span>
                <button
                  disabled={saving}
                  onClick={() => toggleTool(tool.id, !active)}
                  className="rounded border px-3 py-1 text-xs disabled:opacity-50"
                >
                  {active ? "Turn off" : saving ? "Updating…" : "Add tool"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
